// src/common/components/PageMeta/usePageMeta.ts
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getClientConfig, getImageUri } from '../../../config/runtimeConfig';
import { selectCurrentCluster } from '../../../store/slices/currentClusterSlice';
import { ROUTES } from '../../utils/constants';

interface UsePageMetaProps {
  itemName?: string;
  itemDescription?: string;
  itemImageFile?: string;
  itemPrice?: number;
  customTitle?: string;
  type?: 'website' | 'product' | 'article' | 'place';
}

const getDefaultType = (path: string): 'website' | 'product' | 'article' | 'place' => {
  if (path.startsWith(`${ROUTES.ITEM}/`) || path.startsWith(`${ROUTES.GROUP}/`)) {
    return 'product';
  }
  if (path.startsWith(`${ROUTES.PROMO}/`)) {
    return 'article';
  }
  if (path.startsWith(`${ROUTES.CONTACTS}/`)) {
    return 'place';
  }
  return 'website';
};

const getPageTitle = (path: string, tenantName: string, tenantDescription?: string): string => {
  switch (path) {
    case ROUTES.MAIN:
      if (tenantDescription && tenantDescription !== tenantName) {
        return `${tenantName} | ${tenantDescription}`;
      }
      return tenantName;
    case ROUTES.PROMO:
      return `Акции | ${tenantName}`;
    case ROUTES.FAVORITES:
      return `Избранное | ${tenantName}`;
    case ROUTES.PROFILE:
      return `Профиль | ${tenantName}`;
    case ROUTES.CONTACTS:
      return `Контакты | ${tenantName}`;
    case ROUTES.ORDERS:
      return `История заказов | ${tenantName}`;
    case ROUTES.ADDRESSES:
      return `Мои адреса | ${tenantName}`;
    case ROUTES.BONUS:
      return `Бонусы | ${tenantName}`;
    case ROUTES.BONUS_HISTORY:
      return `История бонусов | ${tenantName}`;
    case ROUTES.DELIVERY:
      return `Доставка | ${tenantName}`;
    case ROUTES.CATEGORY:
      return `Категории | ${tenantName}`;
    case ROUTES.PAYMENT_METHODS:
      return `Способы оплаты | ${tenantName}`;
    case ROUTES.PAYMENT:
      return `Оплата заказа | ${tenantName}`;
    case ROUTES.TELEGRAM_LOGIN:
      return `Вход | ${tenantName}`;
    case ROUTES.MAX_LOGIN:
      return `MAX авторизация | ${tenantName}`;
    case ROUTES.EMAIL_LOGIN:
      return `Вход по email | ${tenantName}`;
    case ROUTES.PRIVACY_POLICY:
      return `Политика конфиденциальности | ${tenantName}`;
    case ROUTES.USER_AGREEMENT:
      return `Пользовательское соглашение | ${tenantName}`;
    case ROUTES.PERSONAL_DATA_AGREEMENT:
      return `Согласие на обработку данных | ${tenantName}`;
    default:
      return tenantName;
  }
};

export const usePageMeta = ({
  itemName,
  itemDescription,
  itemImageFile,
  itemPrice,
  customTitle,
  type: explicitType,
}: UsePageMetaProps) => {
  const location = useLocation();
  const config = getClientConfig();
  const cluster = useSelector(selectCurrentCluster);
  const tenantName = cluster?.name || config.name;
  const tenantDescription = cluster?.description || config.description;
  const path = location.pathname;

  const defaultType = getDefaultType(path);

  // Title и Description
  let title = tenantName;
  let description = tenantDescription;

  if (itemName) {
    title = `${itemName} | ${tenantName}`;
    description = itemDescription || tenantDescription;
  } else if (customTitle) {
    title = `${customTitle} | ${tenantName}`;
  } else if (path.startsWith(`${ROUTES.ORDERS}/`)) {
    const orderId = path.split('/')[2];
    title = `Заказ #${orderId} | ${tenantName}`;
    description = `Детали заказа #${orderId}`;
  } else if (path.startsWith(`${ROUTES.CATEGORY}/`)) {
    title = `Категория | ${tenantName}`;
  } else {
    title = getPageTitle(path, tenantName, tenantDescription);
  }

  // URL — текущий path
  const url = `https://${config.domain}${path}`;

  // Изображение
  const image = itemImageFile
    ? `${getImageUri()}${itemImageFile}`
    : `https://${config.domain}/icons/og-image.jpg`;

  // Тип страницы
  const pageType = explicitType || (itemName ? defaultType : 'website');

  return {
    title,
    description,
    image,
    url,
    type: pageType,
    siteName: tenantName,
    price: itemPrice,
  };
};